import axios from 'axios';
import { useEffect, useState } from 'react';
import { utils, writeFile } from 'xlsx';
import 'react-datepicker/dist/react-datepicker.css';
import './MainPage.css';
import Button from '@mui/material/Button';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Table from '../Table/Table.js';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import OutlinedCard from '../Cards/OutlinedCard';
import Grid from '@mui/material/Grid2';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { Box, Snackbar } from '@mui/material';
import { theme } from '../../index';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

const Item = styled(Paper)(({ theme }) => ({
   backgroundColor: theme.palette.secondary,
   ...theme.typography.body2,
   padding: theme.spacing(1),
   textAlign: 'center',
   color: theme.palette.text.secondary,
   ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.champagne.main
   })
}));

const axiosInstance = axios.create({
   baseURL: process.env.REACT_APP_BASE_URL,
   headers: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      Authorization: 'Bearer ' + process.env.REACT_APP_BEARER_TOKEN
   }
});

function MainPage() {
   const [stats, setStats] = useState();
   const [labsData, setLabsData] = useState([]);
   const [startDate, setStartDate] = useState();
   const [endDate, setEndDate] = useState();
   const [filterOpen, setFilterOpen] = React.useState(false);
   const [serverError, setServerError] = React.useState(false);
   const [enableFilter, setEnableFilter] = useState(true);
   const [sortLabid, setSortLabid] = useState('');
   const [isLoading, setIsLoading] = useState(false);

   const handleEnableFilerChange = (event, value) => {
      setEnableFilter(value);
   };

   const handleSortByLabid = () => {
      if (sortLabid === '') setSortLabid('ASC');
      if (sortLabid === 'ASC') setSortLabid('DESC');
      if (sortLabid === 'DESC') setSortLabid('');
   };

   const handleClose = (origin, event, reason) => {
      if (reason === 'clickaway') return;

      if (origin === 'errorSnackBar') setServerError(false);

      if (origin === 'filterSnackBar') setFilterOpen(false);
   };

   const fetchStats = async () => {
      try {
         const response = await axiosInstance.get('/dashboard/stats');
         if (response?.status === 200 && response?.data) setStats(response.data);
      } catch (error) {
         console.log(error);
      }
   };

   const fetchTableData = async (startDate, endDate) => {
      if (labsData !== undefined) setFilterOpen(true);
      setIsLoading(true);
      try {
         const body = {};
         if (startDate && endDate) {
            body.startDate = startDate;
            body.endDate = endDate;
         }
         body.enabled = enableFilter;
         body.sortByLabid = sortLabid;
         const response = await axiosInstance.post('/dashboard/labs', body);

         if (response?.headers['content-type'].includes('text/html')) {
            setServerError(true);
            return;
         }

         if (response?.status === 200 && response?.data) {
            setLabsData(response.data);
            serverError && setServerError(false);
         }
      } catch (error) {
         console.log(error);
      }
      setIsLoading(false);
   };

   useEffect(() => {
      fetchStats();
      fetchTableData();
   }, [enableFilter, sortLabid]);

   const exportDataToExcel = () => {
      const headerRow1 = [
         'ID', // col 0
         'Name', // col 1
         'Labid', // col 2
         'Onboarded', // col 3
         'Enabled', // col 4
         'Testing', // col 5
         'Functional Messages', // col 6 (will span cols 6-11)
         '', // col 7
         '', // col 8
         '', // col 9
         '', // col 10
         '', // col 11
         'Technical Messages', // col 12 (will span cols 12-13)
         '' // col 13
      ];

      const headerRow2 = [
         '',
         '',
         '',
         '',
         '',
         '', // cols 0–5 (non-grouped)
         'Incoming',
         '',
         '', // cols 6–8: Functional Incoming (to be merged horizontally)
         'Outgoing',
         '',
         '', // cols 9–11: Functional Outgoing (to be merged horizontally)
         'Incoming',
         'Outgoing' // cols 12–13: Technical messages
      ];

      const headerRow3 = [
         '',
         '',
         '',
         '',
         '',
         '', // cols 0–5
         'EhubAnalysisRequestwithBox',
         'EhubAnalysisRequestResult',
         'EhubTransportList', // cols 6–8 for Functional Incoming
         'EhubAnalysisRequestwithBox',
         'EhubAnalysisRequestResult',
         'EhubTransportList', // cols 9–11 for Functional Outgoing
         'EhubMetaData',
         'EhubMetaData' // cols 12–13 for Technical messages
      ];

      // Data rows must follow the same order as the headers:
      const dataRows = labsData.map((row) => [
         row.id, // col 0
         row.name, // col 1
         row.labid, // col 2
         row.onboarded, // col 3
         row.enabled, // col 4
         row.testing, // col 5
         // Functional Incoming breakdown:
         row.incomingMsg.functional.EhubAnalysisRequestWithBox,
         row.incomingMsg.functional.EhubAnalysisRequestResult,
         row.incomingMsg.functional.EhubTransportList,
         // Functional Outgoing breakdown:
         row.outgoingMsg.functional.EhubAnalysisRequestWithBox,
         row.outgoingMsg.functional.EhubAnalysisRequestResult,
         row.outgoingMsg.functional.EhubTransportList,
         // Technical messages breakdown:
         row.incomingMsg.technical.EhubMetaData, // Technical Incoming
         row.outgoingMsg.technical.EhubMetaData // Technical Outgoing
      ]);

      const sheetData = [headerRow1, headerRow2, headerRow3, ...dataRows];

      // Create a worksheet from array-of-arrays
      const workSheet = utils.aoa_to_sheet(sheetData);

      //  (r: row, c: column)
      workSheet['!merges'] = [
         // Merge non-grouped headers vertically across all header rows (rows 0 to 2)
         { s: { r: 0, c: 0 }, e: { r: 2, c: 0 } }, // "ID"
         { s: { r: 0, c: 1 }, e: { r: 2, c: 1 } }, // "Name"
         { s: { r: 0, c: 2 }, e: { r: 2, c: 2 } }, // "Labid"
         { s: { r: 0, c: 3 }, e: { r: 2, c: 3 } }, // "Onboarded"
         { s: { r: 0, c: 4 }, e: { r: 2, c: 4 } }, // "Enabled"
         { s: { r: 0, c: 5 }, e: { r: 2, c: 5 } }, // "Testing"

         // Functional Messages group:
         // Merge HeaderRow1 for functional messages: columns 6 to 11 in row 0.
         { s: { r: 0, c: 6 }, e: { r: 0, c: 11 } },
         // In HeaderRow2, merge for Functional Incoming: columns 6 to 8.
         { s: { r: 1, c: 6 }, e: { r: 1, c: 8 } },
         // In HeaderRow2, merge for Functional Outgoing: columns 9 to 11.
         { s: { r: 1, c: 9 }, e: { r: 1, c: 11 } },

         // Technical Messages group:
         // Merge HeaderRow1 for technical messages: columns 12 to 13 in row 0.
         { s: { r: 0, c: 12 }, e: { r: 0, c: 13 } }
         // For technical messages, we leave HeaderRow2 and HeaderRow3 as-is so both labels appear.
      ];

      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, workSheet, 'Data');
      writeFile(workbook, 'labsData.xlsx');
   };

   const isValidDate = (date) => {
      if (date instanceof Date && !isNaN(date.getTime())) {
         return true;
      }
      if (typeof date === 'string') {
         const parsedDate = new Date(date);
         return !isNaN(parsedDate.getTime());
      }
      return false;
   };

   const filterOutByDate = () => {
      if (isValidDate(startDate) && isValidDate(endDate)) {
         fetchTableData(startDate, endDate);
      } else {
         alert('Please select a valid start and end date');
      }
   };

   const onChangeStartDate = (date) => {
      if (date && isValidDate(date.toDate())) {
         setStartDate(date.toISOString());
      } else {
         setStartDate(null);
      }
   };

   const onChangeEndDate = (date) => {
      if (date && isValidDate(date.toDate())) {
         setEndDate(date.toISOString());
      } else {
         setEndDate(null);
      }
   };
   return (
      <>
         <div>
            <Typography variant='h3' component='div' color='yellow' fontWeight={'bolder'}>
               DTHS Dashboard
            </Typography>
         </div>

         <div className={'container'}>
            {serverError && (
               <Snackbar
                  sx={(theme) => ({
                     bgcolor: theme.palette.yellow.main
                  })}
                  color='warning'
                  open={serverError}
                  autoHideDuration={5000}
                  onClose={() => handleClose('errorSnackBar')}
                  message={'Bad response from server. Check the server or the configuration files!'}
                  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
               />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
               <div className={'card-container'}>
                  <Grid container spacing={2} justifyContent={'center'}>
                     <Grid size={{ xs: 12, md: 6, lg: 2.4 }}>
                        <Item sx={{ backgroundColor: '#ffc60d', height: '100%' }}>
                           <OutlinedCard
                              title={'Connected Labs'}
                              body={stats?.connectedLabs ?? 0}
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'center',
                                 alignItems: 'center'
                              }}
                           />
                        </Item>
                     </Grid>
                     <Grid size={{ xs: 12, md: 6, lg: 2.4 }}>
                        <Item sx={{ backgroundColor: '#ffc60d', height: '100%' }}>
                           <OutlinedCard
                              title={'Enabled Labs'}
                              body={stats?.enabledLabs ?? 0}
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'center',
                                 alignItems: 'center'
                              }}
                           />
                        </Item>
                     </Grid>
                     <Grid size={{ xs: 12, md: 6, lg: 2.4 }}>
                        <Item sx={{ backgroundColor: '#ffc60d', height: '100%' }}>
                           <OutlinedCard
                              title={'Incoming Messages'}
                              body={stats?.incomingMessages ?? 0}
                           />
                        </Item>
                     </Grid>
                     <Grid size={{ xs: 12, md: 6, lg: 2.4 }}>
                        <Item sx={{ backgroundColor: '#ffc60d', height: '100%' }}>
                           <OutlinedCard
                              title={'Outgoing Messages'}
                              body={stats?.outgoingMessages ?? 0}
                              sx={{ backgroundColor: '#ffc60d', height: '100%' }}
                           />
                        </Item>
                     </Grid>
                  </Grid>
               </div>
               <Grid size={{ xs: 12, md: 6, lg: 2.4 }}>
                  <Item sx={{ backgroundColor: '#ffc60d', height: '100%', width: '30%', marginX: 'auto' }}>
                     <OutlinedCard
                        title={'Message Types'}
                        array={stats?.numberOfMessagesPerGroupType}
                     />
                  </Item>
               </Grid>
            </Box>

            <div className={'buttons-container'}>
               <div className={'datepicker-container'}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                     <DatePicker
                        label={'Start date'}
                        slotProps={{
                           openPickerButton: { color: 'yellow' },
                           textField: {
                              variant: 'filled',
                              focused: true,
                              color: 'yellow'
                           }
                        }}
                        onChange={onChangeStartDate}
                     />
                  </LocalizationProvider>
               </div>
               <div className={'datepicker-container'}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                     <DatePicker
                        label={'End date'}
                        slotProps={{
                           openPickerButton: { color: theme.palette.yellow.main },
                           textField: {
                              variant: 'filled',
                              focused: true,
                              color: 'yellow'
                           }
                        }}
                        onChange={onChangeEndDate}
                     />
                  </LocalizationProvider>
               </div>
               <Snackbar
                  sx={(theme) => ({
                     background: theme.palette.yellow.main
                  })}
                  color='warning'
                  open={filterOpen}
                  autoHideDuration={3000}
                  onClose={() => handleClose('filterSnackBar')}
                  message={'Filter applied, data is being loaded!'}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
               />
               <div>
                  <Button variant='outlined' color={'yellow'} onClick={filterOutByDate}>
                     Filter by date
                  </Button>
                  <Button variant='outlined' color={'yellow'} onClick={exportDataToExcel}>
                     Export as Excel file
                  </Button>
               </div>
            </div>
            <div
               style={{
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center',
                  justifyContent: 'space-evenly'
               }}
            >
               <div>
                  <ToggleButtonGroup
                     color='yellow'
                     value={enableFilter}
                     exclusive
                     disabled={isLoading}
                     onChange={handleEnableFilerChange}
                  >
                     <ToggleButton color='yellow' value={true}>
                        Display enabled
                     </ToggleButton>
                     <ToggleButton value={false}>Display all</ToggleButton>
                  </ToggleButtonGroup>
               </div>
               <div>
                  <Button
                     variant='contained'
                     color='yellow'
                     disabled={isLoading}
                     onClick={handleSortByLabid}
                  >
                     {sortLabid === '' && 'Sort by Labid'}
                     {sortLabid === 'ASC' && 'Labid by ASC'}
                     {sortLabid === 'DESC' && 'Labid by DESC'}
                  </Button>
               </div>
            </div>
            <div className={'table-container'}>
               <Table data={labsData} />
            </div>
         </div>
      </>
   );
}

export default MainPage;
